import { Link } from 'react-router-dom'
import ReactMarkdown, { type Components } from 'react-markdown'

/**
 * The markdown component map — the one place a post's markdown becomes design-system DOM.
 *
 * Most elements are deliberately absent. Post bodies render inside
 * `.ul-donation-details-summary-txt.ul-blog-details-txt`, and that wrapper styles `p`,
 * `ul`, `ul li` and `blockquote` by element rather than by class, so emitting a bare tag
 * is what picks the design system's styling up. Only the cases below need to say
 * anything, and each says the least it can.
 *
 * **Promoted out of `app_website` on 2026-09-21**, which is the day a second app grew post
 * bodies — the blog is one library on four sites now, so the thing that renders a post body
 * has to be one component. `react-markdown` moved with it and is a dependency of
 * `@kkf/design` rather than of each app, for the same reason.
 */
const COMPONENTS: Components = {
  /*
   * `.ul-about-tab-list` — the class the hand-written post bodies carried before the
   * content moved to markdown, kept so the rendering does not shift. The wrapper styles
   * a bare `ul` too, with a different bullet; this class is the one that was here.
   */
  ul: ({ node: _node, ...props }) => <ul className="ul-about-tab-list" {...props} />,

  /*
   * Internal links go through the router so they do not reload the app; anything else
   * is treated as leaving the site. `href` is optional in the markdown AST, so a link
   * with no target degrades to a plain anchor rather than throwing.
   */
  a: ({ node: _node, href, children, ...props }) => {
    if (href?.startsWith('/')) {
      return (
        <Link to={href} {...props}>
          {children}
        </Link>
      )
    }
    const external = href?.startsWith('http')
    return (
      <a href={href} {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})} {...props}>
        {children}
      </a>
    )
  },

  // Body images are below the fold by definition — the hero is the post's own `image`.
  img: ({ node: _node, ...props }) => <img loading="lazy" {...props} />,
}

/** Renders one post's markdown body. */
export function Markdown({ children }: { children: string }) {
  return <ReactMarkdown components={COMPONENTS}>{children}</ReactMarkdown>
}
